import { computed, ref, watch, type Ref } from 'vue';
import {
    projectActiveFields,
    type FrontendFieldDefinition,
    type FrontendFormDefinition
} from '@hmmh/storyblok-forms/client';
import type { FormInputs } from '~/utils/formUtils';

const initialValue = (field: FrontendFieldDefinition): string | boolean => {
    // Existing top-level checkboxes start unchecked, regardless of their configured value.
    if (field.type === 'checkbox' && !field.activation) return false;

    if (field.type === 'checkbox' || field.type === 'medice-consent') return field.value === 'true';

    return field.value ?? '';
};

const fieldIdentity = (field: FrontendFieldDefinition) =>
    JSON.stringify({
        type: field.type,
        value: field.value,
        disabled: field.disabled,
        activation: field.activation,
        options: field.options?.map((option) => option.key)
    });

export const useConditionalFormState = (definition: Readonly<Ref<FrontendFormDefinition | null | undefined>>) => {
    const formInputs = ref<FormInputs>(Object.create(null) as FormInputs);
    let previousFields: FrontendFieldDefinition[] = [];
    let reconciling = false;

    const allRawData = () =>
        Object.fromEntries(Object.entries(formInputs.value).map(([name, input]) => [name, String(input.value)]));

    const projection = computed(() => {
        try {
            return {
                value: definition.value ? projectActiveFields(allRawData(), definition.value) : undefined,
                error: undefined
            };
        } catch (cause) {
            return { value: undefined, error: new Error('Unable to evaluate form fields', { cause }) };
        }
    });
    const projectionError = computed(() => projection.value.error);
    const activeFields = computed(() => projection.value.value?.fields ?? []);
    const activeValues = computed(() => projection.value.value?.normalizedData ?? {});

    const resetFields = (names: Set<string>) => {
        for (const field of definition.value?.fields ?? []) {
            if (names.has(field.name)) formInputs.value[field.name] = { value: initialValue(field) };
        }
    };

    const descendants = (selectors: Set<string>, fields: FrontendFieldDefinition[]) => {
        const names = new Set<string>();

        // Definitions are bounded and cycle-checked by the core schema.
        for (let remaining = fields.length; remaining > 0; remaining--) {
            let added = false;

            for (const field of fields) {
                if (
                    field.activation &&
                    (selectors.has(field.activation.selector) || names.has(field.activation.selector)) &&
                    !names.has(field.name)
                ) {
                    names.add(field.name);
                    added = true;
                }
            }

            if (!added) break;
        }

        return names;
    };

    watch(
        definition,
        (next) => {
            reconciling = true;

            try {
                const fields = next?.fields ?? [];
                const oldFields = new Map(previousFields.map((field) => [field.name, field]));
                const changed = new Set(
                    fields
                        .filter((field) => {
                            const previous = oldFields.get(field.name);

                            return !previous || fieldIdentity(previous) !== fieldIdentity(field);
                        })
                        .map((field) => field.name)
                );
                const resetNames = new Set([
                    ...changed,
                    ...descendants(changed, previousFields),
                    ...descendants(changed, fields)
                ]);
                const nextInputs = Object.create(null) as FormInputs;

                for (const field of fields) {
                    nextInputs[field.name] =
                        !resetNames.has(field.name) && formInputs.value[field.name]
                            ? formInputs.value[field.name]!
                            : { value: initialValue(field) };
                }

                formInputs.value = nextInputs;
                previousFields = JSON.parse(JSON.stringify(fields)) as FrontendFieldDefinition[];
            } finally {
                reconciling = false;
            }
        },
        { immediate: true, deep: true, flush: 'sync' }
    );

    const conditionalSelectorValues = computed(() =>
        (definition.value?.fields ?? [])
            .filter((field) => field.type === 'conditional')
            .map((field) => [field.name, formInputs.value[field.name]?.value] as const)
    );

    watch(
        conditionalSelectorValues,
        (next, previous) => {
            if (reconciling || !previous) return;

            const previousValues = new Map(previous);
            const changed = new Set(
                next
                    .filter(([name, value]) => previousValues.has(name) && previousValues.get(name) !== value)
                    .map(([name]) => name)
            );

            if (!changed.size) return;

            reconciling = true;

            try {
                resetFields(descendants(changed, definition.value?.fields ?? []));

                for (const name of changed) {
                    const input = formInputs.value[name];

                    if (input) input.errors = [];
                }
            } finally {
                reconciling = false;
            }
        },
        { flush: 'sync' }
    );

    const reset = () => {
        reconciling = true;

        try {
            resetFields(new Set((definition.value?.fields ?? []).map((field) => field.name)));
        } finally {
            reconciling = false;
        }
    };

    const clearValidationErrors = () => {
        for (const input of Object.values(formInputs.value)) input.errors = [];
    };

    const validationErrorCount = computed(
        () => activeFields.value.filter((field) => formInputs.value[field.name]?.errors?.length).length
    );

    const toRawData = (): Record<string, string> => {
        if (projectionError.value) throw projectionError.value;

        return projection.value.value?.rawData ?? {};
    };

    return {
        formInputs,
        activeFields,
        activeValues,
        projectionError,
        reset,
        clearValidationErrors,
        validationErrorCount,
        toRawData
    };
};
